import { useCallback, useState } from "react";
import {
  Form,
  Input,
  Button,
  Typography,
  message,
  Segmented,
  Result,
} from "antd";
import PhoneInput from "../../components/shared/PhoneInput";
import httpClient from "../../api/httpClient";
import { loginThunk } from "../../store/user/thunks";
import { useDispatch } from "react-redux";
import { StyledSigninDiv } from "./styles";
import { ROUTES } from "../../constant/routes";
import { Link, useNavigate } from "react-router-dom";
import { parseError } from "../../utils/shared";

const Signin = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [phoneData, setPhoneData] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Step 1: Send OTP
  const handleSendOtp = useCallback(async (values) => {
    try {
      setLoading(true);

      const payload = {
        phone_country_code: values?.mobile_number.phone_country_code,
        phone: values?.mobile_number?.phone,
        type: values?.type || "otp",
      };

      await httpClient.publicPost("customer/sign-in", payload);

      setPhoneData(payload);

      if (values?.type === "magicLink") {
        message.success(
          "A secure sign-in link has been sent to your mobile number.",
        );
        setMagicLinkSent(true);
        return;
      }

      setOtpSent(true);

      message.success("OTP sent successfully!");
    } catch (error) {
      //@ts-ignore
      parseError(error, true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Step 2: Verify OTP and Login
  const handleVerifyOtp = useCallback(
    async (values) => {
      try {
        setLoading(true);

        const { payload } = await dispatch(
          loginThunk({
            phone_country_code: phoneData.phone_country_code,
            phone: phoneData?.phone,
            code: values.password,
          }),
        );

        if (payload?.id) {
          message.success("Login successful!");
          navigate(ROUTES.CHATS);
        }
      } catch (error) {
        //@ts-ignore
        message.error(error.message || "Login failed. Please try again.");
        form.setFields([
          {
            name: "password",
            errors: ["Invalid OTP. Please try again."],
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [phoneData, form],
  );

  // Main form submit handler
  const onFinish = useCallback(
    async (values) => {
      if (!otpSent) {
        // First step: Send OTP
        await handleSendOtp(values);
      } else {
        // Second step: Verify OTP
        await handleVerifyOtp(values);
      }
    },
    [otpSent, handleSendOtp, handleVerifyOtp],
  );

  // Reset to phone input step
  const handleChangePhone = useCallback(() => {
    setOtpSent(false);
    setPhoneData(null);
    form.resetFields(["password"]);
  }, [form]);

  return (
    //@ts-ignore
    <StyledSigninDiv className="auth-right">
      <div className="right-header">
        <h2>Customer Portal 🔐</h2>
        <p>Sign in to continue to your dashboard</p>
      </div>

      <Form
        form={form}
        layout="vertical"
        disabled={loading}
        onFinish={onFinish}
      >
        {!otpSent && !magicLinkSent && (
          <Form.Item label="Sign-in Method" name="type" initialValue="otp">
            <Segmented
              name="type"
              block
              options={[
                {
                  label: "OTP",
                  value: "otp",
                },
                {
                  label: "Sign-in Link",
                  value: "magicLink",
                },
              ]}
            />
          </Form.Item>
        )}

        {/* Phone Input - Hidden after OTP is sent */}

        {!magicLinkSent && (
          <>
            {!otpSent ? (
              <PhoneInput split />
            ) : (
              <Form.Item label="Phone Number">
                <div className="flex align-center justify-between">
                  <Typography.Text strong>
                    {phoneData?.phone_country_code} {phoneData?.phone}
                  </Typography.Text>
                  <Button
                    type="link"
                    onClick={handleChangePhone}
                    disabled={loading}
                  >
                    Change
                  </Button>
                </div>
              </Form.Item>
            )}
          </>
        )}
        {/* OTP Input - Only shown after OTP is sent */}
        {otpSent && (
          <Form.Item
            label="Enter Login OTP"
            name="password"
            rules={[
              { required: true, message: "Please enter the OTP!" },
              { len: 6, message: "OTP must be 6 digits!" },
            ]}
          >
            <Input.OTP length={6} type="tel" />
          </Form.Item>
        )}

        {magicLinkSent ? (
          <Result
            status="success"
            title="Sign-in Link Sent"
            subTitle="We've sent a secure sign-in link to your mobile number. Open the SMS and tap the link to continue."
            extra={[
              <Button
                key="back"
                onClick={() => {
                  setMagicLinkSent(false);
                  form.setFieldValue("type", "otp");
                }}
              >
                Sign in with OTP
              </Button>,
            ]}
          />
        ) : (
          <Form.Item style={{ marginTop: "2rem" }}>
            <Button type="primary" htmlType="submit" loading={loading} block>
              {otpSent ? "Verify & Login" : "Send"}
            </Button>
          </Form.Item>
        )}

        {/* Resend OTP option */}
        {otpSent && (
          <div className="text-center" style={{ marginBottom: "1rem" }}>
            <Typography.Link
              onClick={() => handleSendOtp({ mobile_number: phoneData })}
              disabled={loading}
            >
              Resend OTP
            </Typography.Link>
          </div>
        )}

        <div className="text-gray login-link text-center">
          Don't have an account?
          <Link to={ROUTES.SIGNUP}>
            <Typography.Link className="text-primary" disabled={loading}>
              &nbsp;Sign Up
            </Typography.Link>
          </Link>
        </div>
      </Form>
    </StyledSigninDiv>
  );
};

export default Signin;
